import { acceptHMRUpdate, defineStore } from 'pinia'
import { nextTick, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'

import {
  isPracticeActive,
  PRACTICE_QUERY_SCOPE,
} from 'src/composables/onboarding/practice/practiceMode'
import { buildExampleAccount } from 'src/composables/onboarding/practice/practiceSource'
import {
  clearExampleData,
  getSceneKey,
  leaveScreens,
  prepareScreen,
  waitForTargets,
} from 'src/composables/onboarding/tour-screens'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { log } from 'src/services/app/log.service'
import { sessionStorageService } from 'src/services/app/storage.service'
import { ANALYTICS_EVENTS, STORAGE_KEYS } from 'src/services/consts/api.const'
import { ONBOARDING_STORY, TOUR_STATUS } from 'src/services/consts/onboarding.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { ROUTES } from 'src/router/const'
import { useAccountActionsStore } from 'src/stores/account/account-actions'
import { useOnboardingTourStore } from 'src/stores/onboarding'
import { useNewTicketStore } from 'src/stores/ticket/new-ticket'
import { useTicketsStore } from 'src/stores/tickets'

// The only writer of the tour store. Every exit goes through endTour.
export const useOnboardingActionsStore = defineStore('onboarding-actions', () => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const tourStore = useOnboardingTourStore()
  const accountActionsStore = useAccountActionsStore()
  const newTicketStore = useNewTicketStore()
  const ticketsStore = useTicketsStore()
  const { captureEvent } = usePostHog()

  async function requestReplay() {
    // A second click would supersede the first navigation, which resolves as a failure.
    if (tourStore.isReplay) return
    tourStore.isReplay = true
    try {
      const failure = await router.push({ name: ROUTES.HOME })
      if (failure) tourStore.isReplay = false
    } catch (error) {
      tourStore.isReplay = false
      log.error('Onboarding walkthrough replay could not open the Dashboard', error)
    }
  }

  // Capture phase, so Escape works wherever focus is.
  function onKeydown(event) {
    if (event.key !== 'Escape') return
    if (!tourStore.canSkip) {
      closeTour()
      return
    }
    if (tourStore.isSkipConfirmOpen) keepTour()
    else askToSkip()
  }

  function startTour() {
    const realAccount = queryClient.getQueryData([QUERY_KEYS.ACCOUNT_DATA])
    const exampleAccount = buildExampleAccount(realAccount)
    if (!exampleAccount) return

    // Seed before the flag switches the account query to this key, so no loader shows.
    queryClient.setQueryData([PRACTICE_QUERY_SCOPE, QUERY_KEYS.ACCOUNT_DATA], exampleAccount)
    isPracticeActive.value = true
    tourStore.hasAutoStarted = true
    tourStore.cardIndex = 0
    tourStore.status = TOUR_STATUS.CHANGING
    document.getElementById('q-app')?.setAttribute('inert', '')
    // Not capture: the router starts the Back navigation first, and leaveScreens supersedes it.
    window.addEventListener('popstate', endTour)
    window.addEventListener('keydown', onKeydown, true)
    showCard({ index: 0, direction: 1 })
  }

  // A card whose targets never appear is skipped in the direction of travel.
  async function showCard({ index, direction }) {
    const card = ONBOARDING_STORY[index]
    if (!card) {
      await endTour()
      return
    }

    try {
      const cardSceneKey = getSceneKey(card)
      const isSameScreen = tourStore.sceneKey === cardSceneKey
      tourStore.pendingIndex = index
      tourStore.status = isSameScreen ? TOUR_STATUS.MOVING : TOUR_STATUS.CHANGING

      await prepareScreen({ card, router, queryClient, newTicketStore })
      if (!tourStore.isMoveActive) return

      const result = await waitForTargets({
        anchors: card.anchors,
        shouldKeepWaiting: () => tourStore.isMoveActive,
      })
      if (!tourStore.isMoveActive) return
      if (result === 'missing') {
        await showCard({ index: index + direction, direction })
        return
      }

      if (!isSameScreen) document.activeElement?.blur()
      tourStore.cardIndex = index
      tourStore.status = TOUR_STATUS.SHOWING
    } catch (error) {
      log.error('Onboarding walkthrough could not show a card', error)
      await endTour()
    }
  }

  async function nextCard() {
    if (tourStore.status !== TOUR_STATUS.SHOWING) return

    if (tourStore.cardIndex === ONBOARDING_STORY.length - 1) {
      await finishTour(ANALYTICS_EVENTS.ONBOARDING_TOUR_DONE)
      return
    }
    if (!tourStore.hasLeftWelcome) {
      tourStore.hasLeftWelcome = true
      captureEvent(ANALYTICS_EVENTS.ONBOARDING_TOUR_STARTED, { isReplay: tourStore.isReplay })
    }
    await showCard({ index: tourStore.cardIndex + 1, direction: 1 })
  }

  function askToSkip() {
    tourStore.isSkipConfirmOpen = true
  }

  function keepTour() {
    tourStore.isSkipConfirmOpen = false
  }

  // "Don't show again"
  function skipTour() {
    finishTour(ANALYTICS_EVENTS.ONBOARDING_TOUR_SKIPPED)
  }

  // "Skip for now": nothing is saved, so the tour returns on the next sign-in.
  function snoozeTour() {
    captureEvent(ANALYTICS_EVENTS.ONBOARDING_TOUR_SNOOZED, { isReplay: tourStore.isReplay })
    sessionStorageService.set(STORAGE_KEYS.ONBOARDING_TOUR_SNOOZED, true)
    endTour()
  }

  // The save is not awaited: the cache already holds the new value, and the mutation reports failures.
  async function finishTour(event) {
    captureEvent(event, { isReplay: tourStore.isReplay })
    accountActionsStore.dismissOnboardingTour()
    await endTour()
  }

  async function previousCard() {
    if (tourStore.status !== TOUR_STATUS.SHOWING || tourStore.cardIndex === 0) return
    await showCard({ index: tourStore.cardIndex - 1, direction: -1 })
  }

  function closeTour() {
    if (!tourStore.canClose) return
    endTour()
  }

  async function endTour() {
    if (tourStore.status === TOUR_STATUS.ENDING) return

    tourStore.status = TOUR_STATUS.ENDING
    try {
      await leaveScreens({ router, newTicketStore, ticketsStore })
    } catch (error) {
      log.error('Onboarding walkthrough could not leave its screens', error)
    } finally {
      await resetTourState()
    }
    document.querySelector('main')?.focus()
  }

  // Must not navigate: the host calls it on unmount. The tick lets the account query switch back
  // to the real key before the example data is cleared.
  async function resetTourState() {
    isPracticeActive.value = false
    window.removeEventListener('popstate', endTour)
    window.removeEventListener('keydown', onKeydown, true)
    document.getElementById('q-app')?.removeAttribute('inert')
    tourStore.isReplay = false
    tourStore.isSkipConfirmOpen = false
    tourStore.hasLeftWelcome = false
    tourStore.status = TOUR_STATUS.IDLE
    await nextTick()
    clearExampleData({ queryClient })
  }

  // Logout, through resetStores.
  function resetState() {
    resetTourState()
    tourStore.cardIndex = 0
    tourStore.pendingIndex = 0
    tourStore.hasAutoStarted = false
  }

  watch(
    () => tourStore.canAutoStart,
    (canStart) => {
      if (canStart) startTour()
    },
    { immediate: true }
  )

  watch(
    () => tourStore.canContinueTour,
    (canContinue) => {
      if (!canContinue) endTour()
    }
  )

  return {
    requestReplay,
    nextCard,
    previousCard,
    askToSkip,
    keepTour,
    skipTour,
    snoozeTour,
    closeTour,
    resetTourState,
    resetState,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOnboardingActionsStore, import.meta.hot))
}
